import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Building, Upload, ImageIcon } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { empresasAPI } from '../../services/api';

const API_BASE_URL = 'http://localhost:5000';

const GerenciarEmpresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    logo: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    carregarEmpresas();
  }, []);

  const carregarEmpresas = async () => {
    try {
      setLoading(true);
      const response = await empresasAPI.listar();
      setEmpresas(response.data.empresas || []);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      alert('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmpresas = empresas.filter(empresa =>
    empresa.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    empresa.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const abrirModal = (mode, empresa = null) => {
    setModalMode(mode);
    setSelectedEmpresa(empresa);
    
    if (mode === 'edit' && empresa) {
      setFormData({
        nome: empresa.nome || '',
        descricao: empresa.descricao || '',
        logo: empresa.logo || ''
      });
    } else {
      setFormData({
        nome: '',
        descricao: '',
        logo: ''
      });
    }
    
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setSelectedEmpresa(null);
    setFormData({
      nome: '',
      descricao: '',
      logo: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    setSubmitting(true);
    
    try {
      if (modalMode === 'create') {
        await empresasAPI.criar(formData);
        alert('Empresa criada com sucesso!');
      } else {
        await empresasAPI.atualizar(selectedEmpresa.id, formData);
        alert('Empresa atualizada com sucesso!');
      }
      
      fecharModal();
      carregarEmpresas();
    } catch (error) {
      console.error('Erro ao salvar empresa:', error);
      const errorMsg = error.response?.data?.erro || 'Erro ao salvar empresa';
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (empresaId, empresaNome) => {
    if (!window.confirm(`Tem certeza que deseja deletar a empresa "${empresaNome}"?`)) {
      return;
    }

    try {
      await empresasAPI.deletar(empresaId);
      alert('Empresa deletada com sucesso!');
      carregarEmpresas();
    } catch (error) {
      console.error('Erro ao deletar empresa:', error);
      const errorMsg = error.response?.data?.erro || 'Erro ao deletar empresa';
      alert(errorMsg);
    }
  };

  const handleUploadLogo = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);

    setUploadingLogo(true);
    
    try {
      const response = await empresasAPI.uploadLogo(formData);
      setFormData(prev => ({
        ...prev,
        logo: response.data.filename
      }));
      alert('Logo enviado com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar logo:', error);
      alert('Erro ao enviar logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Gerenciar Empresas</h1>
          <p className="text-slate-400">Gerencie as empresas da plataforma</p>
        </div>
        <Button
          onClick={() => abrirModal('create')}
          className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Empresa
        </Button>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar empresas por nome ou descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </CardContent>
      </Card>

      {filteredEmpresas.length === 0 ? (
        <Card className="p-12 bg-slate-800/50 border-slate-700/50 text-center">
          <Building className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">
            {searchTerm ? 'Nenhuma empresa encontrada' : 'Nenhuma empresa cadastrada'}
          </h3>
          <p className="text-slate-400 mb-6">
            {searchTerm ? 'Tente alterar os termos de busca' : 'Crie sua primeira empresa para começar'}
          </p>
          {!searchTerm && (
            <Button 
              onClick={() => abrirModal('create')}
              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeira Empresa
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEmpresas.map((empresa) => (
            <Card key={empresa.id} className="bg-slate-800/50 border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/70 transition-all duration-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {empresa.logo ? (
                      <img 
                        src={`${API_BASE_URL}/uploads/${empresa.logo}`}
                        alt={empresa.nome}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                        <Building className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-white">{empresa.nome}</h3>
                      <Badge className="bg-slate-700/50 text-slate-300 border border-slate-600">
                        ID: {empresa.id}
                      </Badge>
                    </div>
                  </div>
                </div>

                {empresa.descricao && (
                  <p className="text-slate-300 text-sm mb-4 line-clamp-3">
                    {empresa.descricao}
                  </p>
                )}

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => abrirModal('edit', empresa)}
                    className="flex-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-600/50"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDelete(empresa.id, empresa.nome)}
                    className="bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 p-6 rounded-lg w-full max-w-lg border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">
              {modalMode === 'create' ? 'Criar Nova Empresa' : 'Editar Empresa'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nome da empresa"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Descrição da empresa..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Logo
                </label>
                <div className="space-y-2">
                  {formData.logo && (
                    <div className="flex items-center space-x-3 p-3 bg-slate-700/50 rounded-lg">
                      <img 
                        src={`${API_BASE_URL}/uploads/${formData.logo}`}
                        alt="Logo preview"
                        className="w-12 h-12 rounded object-cover"
                      />
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">Logo atual</p>
                        <p className="text-xs text-slate-400">{formData.logo}</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setFormData(prev => ({ ...prev, logo: '' }))}
                        className="bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/50"
                      >
                        Remover
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadLogo}
                      className="hidden"
                      id="logo-upload"
                      disabled={uploadingLogo}
                    />
                    <label
                      htmlFor="logo-upload"
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-white hover:bg-slate-600 cursor-pointer transition-colors"
                    >
                      {uploadingLogo ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {uploadingLogo ? 'Enviando...' : 'Escolher Logo'}
                    </label>
                  </div>
                  <p className="text-xs text-slate-400">
                    Formatos aceitos: PNG, JPG, JPEG, GIF
                  </p>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {submitting ? 'Salvando...' : (modalMode === 'create' ? 'Criar' : 'Salvar')}
                </Button>
                <Button 
                  type="button"
                  onClick={fecharModal}
                  className="flex-1 bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciarEmpresas; 